import {
  BASE_PATH,
  SSO_CLOCK_SKEW
} from 'shared/variables';
import { UserManager } from 'oidc-client';
import { toastr } from 'react-redux-toastr';
import {
  getJsonAsync,
  getJsonNonCacheAsync,
  navigateTo
} from 'shared/actions';
import {
  setEmailInState,
  setRoleForUserInState,
  setPermissionForUserInState,
  setDisplayNameInState,
  setProfileValidationsInState,
  setDobInState,
  setGroupNameInState,
  setIsAllowedInState,
  setIsValidTypeInState,
  setMaintenanceMessage,
  setLoginAttempted
} from './actionTypes';
import {
  CLIENT_ID,
  GROUP_ENABLED,
  SIGNIN_CALLBACK_URI,
  SIGNIN_SILENT_RENEW_URI,
  SIGNIN_RESPONSE_TYPE,
  SIGNIN_SCOPES,
  POST_LOGOUT_REDIRECT_URI,
  SILENT_RENEWAL_SECONDS_BEFORE_EXPIRES
} from './variables';

const defaultRoute = 'registration';

const config = {
  authority: window.IDENTITY_SERVER_BASE_URI,
  client_id: CLIENT_ID,
  redirect_uri: SIGNIN_CALLBACK_URI,
  response_type: SIGNIN_RESPONSE_TYPE,
  scope: SIGNIN_SCOPES,
  post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,

  //Renew access_token values shortly before they expire:
  silent_redirect_uri: SIGNIN_SILENT_RENEW_URI,
  accessTokenExpiringNotificationTime: SILENT_RENEWAL_SECONDS_BEFORE_EXPIRES,
  automaticSilentRenew: true,
  clockSkew: SSO_CLOCK_SKEW
};

const manager = new UserManager(config);

manager.events.addUserSignedOut(() => {
  manager.removeUser();
});

manager.events.addUserUnloaded(() => {
  //Obsolete?
  localStorage.removeItem('cart');
  localStorage.removeItem('adminCart');
});

manager.events.addSilentRenewError(() => {
  manager.signinSilent();
});

export const login = (postLoginRoute) => {
  sessionStorage.setItem('postLoginRoute', postLoginRoute || defaultRoute);

  manager.signinRedirect()
    .catch(() => {
      toastr.warning('Not authenticated!', `Sorry. There was a problem trying to
      log you in. Please try again or contact our support team.`);
    });
};

export const refresh = () => {
  manager.signinRedirect({ extraQueryParams: { auth_nocache: "true" } });
};

export const logout = () => {
  manager.signoutRedirect();
};

export const signinRedirectCallback = () => (dispatch) => {
  manager.signinRedirectCallback()
    .then((user) => {
      const postLoginRoute = sessionStorage.getItem('postLoginRoute') || defaultRoute;

      // clean up url for react-router
      // remove any unexpected sub-directory coming from url pathname
      const redirectUrl = postLoginRoute.replace(/^\/|\/$/g, '')
        .replace(BASE_PATH, '')
        .replace(/^\/|\/$/g, '');

      if (postLoginRoute) {
        sessionStorage.removeItem('postLoginRoute');
      }

      setupUser(dispatch, user);

      dispatch(navigateTo(redirectUrl));
    });
};

export const signinSilentCallback = () => {
  manager.signinSilentCallback();
};

const setupUser = (dispatch, user) => new Promise((resolve, reject) => {
  if (user) {
    dispatch(setEmailInState(user.profile.email));

    dispatch(getJsonNonCacheAsync('authorization/getProfile'))
      .then((profile) => {
        dispatch(setDisplayNameInState(profile));
        dispatch(setDobInState(profile.birthDate));
        dispatch(setProfileValidationsInState(profile));

        dispatch(getJsonNonCacheAsync('authorization/getPartner'))
          .then((partnerObj) => {
            dispatch(setIsAllowedInState(partnerObj.isAllowed));
            dispatch(setIsValidTypeInState(partnerObj.isValidType));
            dispatch(setGroupNameInState(
              partnerObj.partner !== '' && user.profile.groupname !== '' && user.profile.group !== ''
                ? user.profile.groupname
                : null
            ));

            // Fetch roles first
            return dispatch(getJsonAsync('authorization/roles'));
          })
          .then((roles) => {
            dispatch(setRoleForUserInState(roles));

            // Fetch permissions only after roles are done
            return dispatch(getJsonAsync('authorization/permissions'));
          })
          .then((permissions) => {
            dispatch(setPermissionForUserInState(permissions));

            resolve();
          })
          .catch((error) => {
            console.error('Error setting up user:', error);
            reject();
          });


      });
  }
});

export const getLoggedInUser = () => manager.getUser();

export const signinSilent = (dispatch, getState) => {
  manager.signinSilent()
    .then((user) => {
      setupUser(dispatch, user)
        .then(() => {
          dispatch(setLoginAttempted());

          let state = getState();

          if (!!state.shared.auth.email &&
            !state.shared.auth.profileIsConfirmed &&
            state.shared.auth.profileIsWorking) {
            dispatch(navigateTo(`registration/accountCreated`));
          }
        });
    })
    .catch(() => {
      dispatch(setLoginAttempted());
    });
};

export const silentCheckAuth = (nextState, replace, callback) => (dispatch, getState) => new Promise(resolve => {
  if (!nextState || !nextState.routes) { throw new Error('nextState object not provided'); }
  if (!replace || typeof replace !== 'function') { throw new Error('replace method not provided'); }
  if (!callback || typeof callback !== 'function') { throw new Error('callback method not provided'); }

  const currentState = getState();
  let currentEmail = '';

  if (currentState.shared != null && currentState.shared.auth != null) {
    currentEmail = currentState.shared.auth.email;
  }

  manager.getUser()
    .then((user) => {
      let shouldSetup = false;

      if (!user && !currentEmail) {
        manager.signinSilent()
          .then((user) => {
            if (user) {
              shouldSetup = true;
            }
          })
          .catch(() => { });
      } else {
        if (!currentEmail) {
          shouldSetup = true;
        } else {
          if (currentEmail != user.profile.email) {
            shouldSetup = true;
          }
        }
      }

      if (shouldSetup) {
        setupUser(dispatch, user)
          .then(() => {
            callback();
            resolve();
          });
      } else {
        callback();
        resolve();
      }
    });
});

export const checkAuth = (nextState, replace, callback) => (dispatch, getState) => new Promise((resolve, reject) => {
  if (!nextState || !nextState.routes) { throw new Error('nextState object not provided'); }
  if (!replace || typeof replace !== 'function') { throw new Error('replace method not provided'); }
  if (!callback || typeof callback !== 'function') { throw new Error('callback method not provided'); }

  const currentState = getState();
  let currentEmail = '';

  if (currentState.shared != null && currentState.shared.auth != null) {
    currentEmail = currentState.shared.auth.email;
  }

  manager.getUser()
    .then((user) => {
      let shouldSetup = false;

      if (!user) {
        login(nextState.location.pathname);
      } else {
        // user authenticated
        if (!currentEmail) {
          shouldSetup = true;
        } else {
          if (currentEmail != user.profile.email) {
            shouldSetup = true;
          }
        }

        if (shouldSetup) {
          setupUser(dispatch, user)
            .then(() => {
              const roleRoute = nextState.routes.find(item => item.role);
              const roles = getState().shared.auth.roles;

              // verify route against roles in state
              if (roleRoute && roleRoute.role) {
                const rolesToAuthorize = roleRoute.role.split(",");

                if (!roles.some(r => rolesToAuthorize.includes(r))) {
                  callback();
                  reject();
                  return;
                }
              }

              callback();
              resolve();
            });
        } else {
          // allow user to continue because route doesn't require a specific role
          callback();
          resolve();
        }
      }
    });
});

export const authorizationHeader = () => {
  return manager.getUser()
    .then((user) => {
      if (user) {
        return user.access_token
          ? { 'Authorization': `Bearer ${user.access_token}` }
          : {};
      }
    });
};

export const retrieveMaintenanceMessage = () => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`registration/tournament/maintenanceMessage`))
    .then(data => {
      dispatch(setMaintenanceMessage(data.maintenanceMessage));
      resolve();
    })
    .catch(error => reject(error));
});

export const handleMaintenanceComplete = (data) => (dispatch) => {
  if (!data) {
    dispatch(navigateTo(defaultRoute));
  }
};

export const goToProfile = () => {
  const identityUri = window.IDENTITY_SERVER_BASE_URI;

  window.location = `${identityUri}/Manage?clientId=${CLIENT_ID}`;
};

export const goToProfileVerifyAccount = () => {
  const identityUri = window.IDENTITY_SERVER_BASE_URI;

  window.location = `${identityUri}/Manage?clientId=${CLIENT_ID}&sendVerification=true`;
};

export const partnerLogin = () => {
  const hubSelectorBaseUri = window.HUB_SELECTOR_BASE_URI;
  window.location = `${hubSelectorBaseUri}`;
};



// WEBPACK FOOTER //
// ./src/features/shared/auth/actions.js